import React, { useContext } from "react";
import styled from "styled-components";
import BqnContext from "common/BqnContext";
import ActionsContext from "common/ActionsContext";
import help from "help/viewStatusInterfacesLinkState.md";
import DetailModal from "./Detail";
import CaptureModal from "./Capture";
import HandleEnableOrDisable from "./HandleEnableOrDisable";
import { parseInterfaces } from "./api";
import View from "common/View";
import Loading from "common/Loading";
import Table from "common/graphs/Table/Table";
import { atBqnTarget } from "common/api";
import { atoiv6 } from "common/utils";
import { TickOrCross } from "common/graphs/Table/TableRenders";
import withReloading from "common/withReloading";

const TableContainer = styled.div`
  td i.material-icons {
    font-size: 18px;
  }
  button[title="Enable"] {
    i.material-icons {
      color: var(--signal-green) !important;
    }
  }
  button[title="Disable"] {
    i.material-icons {
      color: var(--signal-red) !important;
    }
  }
`;

const doesNothing = () => {};

const Management = ({ is = false }) =>
  is !== true ? null : <i className="material-icons">radio_button_checked</i>;

const getColumns = (mode, onActionClick) => {
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "bqn",
            numeric: true,
          },
        ]
      : []),
    {
      label: "NAME",
      id: "name",
      numeric: false,
      cellClassName: (value, row) =>
        row["type"] === "n/a" ? null : "hyperlink-text",
      onClick: (value, row) => {
        if (row["type"] === "n/a") {
          return;
        }
        return onActionClick("details", { name: value, type: row["type"] });
      },
    },
    {
      label: "TYPE",
      id: "type",
      numeric: false,
    },
    {
      label: "MAC",
      id: "mac",
      numeric: false,
      parse: (value) => !value ? 0 : atoiv6(value),
      parseFilter: (value) => value
    },
    {
      label: "UP",
      id: "enabled",
      numeric: false,
      renderCell: (value) => <TickOrCross ok={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "LINK",
      id: "connected",
      numeric: false,
      renderCell: (value) => <TickOrCross ok={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "MGT",
      id: "management",
      numeric: false,
      renderCell: (value) => <Management is={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Enable",
          icon: "arrow_upward",
          onClick: (row) => {
            if (row["enabled"] === true) {
              return;
            }
            onActionClick("update", { name: row["name"], enable: true });
          },
          disabled: (row) => row["enabled"] === true || row["type"] === "n/a",
          hide: (row) =>
            row["type"] === "loopback" ||
            row["type"] === "n/a" ||
            row["management"] === true,
        },
        {
          label: "Disable",
          icon: "arrow_downward",
          onClick: (row) => {
            if (row["enabled"] === false) {
              return;
            }
            onActionClick("update", { name: row["name"], disable: true });
          },
          disabled: (row) => row["enabled"] !== true || row["type"] === "n/a",
          hide: (row) =>
            row["type"] === "loopback" ||
            row["type"] === "n/a" ||
            row["management"] === true,
        },
        {
          label: "Capture",
          icon: "search",
          onClick: (row) => {
            if (row["enabled"] === false) {
              return;
            }
            onActionClick("capture", {
              name: row["name"],
              type: row["type"],
              bqnId: row["bqn"],
            });
          },
          hide: (row) => row["type"] === "loopback" || row["type"] === "n/a",
        },
      ],
    },
  ];
};

const ListInterfaces = ({ outcome: interfaces = [], onActionClick = doesNothing }) => {
  const { clusterStatus } = useContext(BqnContext);
  const columns = getColumns(clusterStatus?.mode, onActionClick);
  return (
    <TableContainer>
      <Table
        columns={columns}
        data={interfaces}
        exportCSV={false}
        isRowSelectable={false}
        rowsPerPageNr={50}
      />
    </TableContainer>
  );
};

// Every interface of the response reports its BQN, and only the BQN selected
// reports its own, so there is nothing to filter out here.
const doRequest = ({ mode, bqnId }) =>
  ifCl
    .run(atBqnTarget("show interface raw", mode, bqnId))
    .then(parseInterfaces)

const ListInterfacesWithReloading = withReloading(ListInterfaces);

const LinkState = ({ returnView }) => {
  const { clusterStatus, bqnId } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const actions = useContext(ActionsContext);

  const onActionClick = (field, params) => {
    actions.send(`show-interface-${field}`, params);
  };

  return (
    <View returnView={returnView}
      mdHelp={help}
      className="container-lg"
      title="Network Interfaces"
      bodyClassName="padding-20 expect-overflow"
    >
        {/* Nothing is asked while the BQN context has not loaded the BQN
            selected: the command would not carry its option, and the
            response would be that of the whole cluster. */}
        {bqnId === undefined ? (
          <Loading />
        ) : (
          <ListInterfacesWithReloading
            key={bqnId}
            provider={() => doRequest({ mode, bqnId })}
            onActionClick={onActionClick}
          />
        )}
      <CaptureModal />
      <DetailModal />
      <HandleEnableOrDisable />
    </View>
  );
};

export default LinkState;
